import type { TierCountMap } from "../../types/rsvp";

export type TicketSelectionRow = {
  id: string;
  partySize: number;
  tierId: string | null;
};

export type TierOption = { id: string; name: string; price: number; description?: string | null };

export function newSelectionId(): string {
  return `sel-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function createDefaultSelection(tiers: TierOption[]): TicketSelectionRow {
  return {
    id: newSelectionId(),
    partySize: 1,
    tierId: tiers[0]?.id ?? null,
  };
}

export function selectionsToTierCounts(selections: TicketSelectionRow[]): TierCountMap {
  const counts: TierCountMap = {};
  for (const sel of selections) {
    if (!sel.tierId || sel.partySize < 1) continue;
    counts[sel.tierId] = (counts[sel.tierId] ?? 0) + sel.partySize;
  }
  return counts;
}

export function sumSelectionParty(selections: TicketSelectionRow[]): number {
  return selections.reduce((sum, sel) => sum + Math.max(0, sel.partySize), 0);
}

export function computeSelectionTotal(selections: TicketSelectionRow[], tiers: TierOption[]): number {
  const priceById = Object.fromEntries(tiers.map((t) => [t.id, t.price]));
  return selections.reduce((sum, sel) => {
    if (!sel.tierId || sel.partySize < 1) return sum;
    return sum + sel.partySize * (priceById[sel.tierId] ?? 0);
  }, 0);
}

export function formatTicketInr(amount: number): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatTicketSummaryLine(partySize: number, tierCount: number): string {
  const guestLabel = partySize === 1 ? "1 guest" : `${partySize} guests`;
  const tierLabel = tierCount === 1 ? "1 tier" : `${tierCount} tiers`;
  return `${guestLabel} · ${tierLabel}`;
}

export type TierSummaryRow = { tierId: string; name: string; guests: number; amount: number };

/** One "'n' guests · <tier>" row per chosen tier, for the form's cost summary. */
export function summarizeSelectionsByTier(
  selections: TicketSelectionRow[],
  tiers: TierOption[],
): TierSummaryRow[] {
  const tierById = new Map(tiers.map((t) => [t.id, t]));
  const byTier = new Map<string, TierSummaryRow>();
  for (const sel of selections) {
    if (!sel.tierId || sel.partySize < 1) continue;
    const tier = tierById.get(sel.tierId);
    if (!tier) continue;
    const row = byTier.get(sel.tierId) ?? { tierId: sel.tierId, name: tier.name, guests: 0, amount: 0 };
    row.guests += sel.partySize;
    row.amount += sel.partySize * tier.price;
    byTier.set(sel.tierId, row);
  }
  return Array.from(byTier.values());
}

export function countActiveTiers(selections: TicketSelectionRow[]): number {
  return new Set(selections.filter((s) => s.tierId && s.partySize > 0).map((s) => s.tierId)).size;
}
